/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Tool usage aggregation target kind
 */
export const ToolUsageTargetTimeSeriesPointTargetKind = {
  Server: "server",
  LocalTools: "local_tools",
  Skill: "skill",
} as const;
/**
 * Tool usage aggregation target kind
 */
export type ToolUsageTargetTimeSeriesPointTargetKind = ClosedEnum<
  typeof ToolUsageTargetTimeSeriesPointTargetKind
>;

/**
 * Tool usage target type
 */
export const ToolUsageTargetTimeSeriesPointTargetType = {
  HostedMcpServer: "hosted_mcp_server",
  TunneledMcpServer: "tunneled_mcp_server",
  MetaMcpServer: "meta_mcp_server",
  ShadowMcpServer: "shadow_mcp_server",
  LocalTool: "local_tool",
  Skill: "skill",
} as const;
/**
 * Tool usage target type
 */
export type ToolUsageTargetTimeSeriesPointTargetType = ClosedEnum<
  typeof ToolUsageTargetTimeSeriesPointTargetType
>;

/**
 * A time-series bucket for one tool usage target
 */
export type ToolUsageTargetTimeSeriesPoint = {
  /**
   * Number of tool usage events denied by a hook in the bucket
   */
  blockedCount: number;
  /**
   * Bucket start time in Unix nanoseconds as a string for JavaScript integer safety
   */
  bucketStartNs: string;
  /**
   * Number of tool usage events in the bucket
   */
  eventCount: number;
  /**
   * Number of failed tool usage events in the bucket
   */
  failureCount: number;
  /**
   * Number of tool usage events observed starting but never finishing in the bucket
   */
  pendingCount: number;
  /**
   * Stable target identifier used by filters and chart grouping
   */
  targetId: string;
  /**
   * Tool usage aggregation target kind
   */
  targetKind: ToolUsageTargetTimeSeriesPointTargetKind;
  /**
   * User-facing label for the target
   */
  targetLabel: string;
  /**
   * Tool usage target type
   */
  targetType: ToolUsageTargetTimeSeriesPointTargetType;
};

/** @internal */
export const ToolUsageTargetTimeSeriesPointTargetKind$inboundSchema:
  z.ZodMiniEnum<typeof ToolUsageTargetTimeSeriesPointTargetKind> = z.enum(
    ToolUsageTargetTimeSeriesPointTargetKind,
  );

/** @internal */
export const ToolUsageTargetTimeSeriesPointTargetType$inboundSchema:
  z.ZodMiniEnum<typeof ToolUsageTargetTimeSeriesPointTargetType> = z.enum(
    ToolUsageTargetTimeSeriesPointTargetType,
  );

/** @internal */
export const ToolUsageTargetTimeSeriesPoint$inboundSchema: z.ZodMiniType<
  ToolUsageTargetTimeSeriesPoint,
  unknown
> = z.pipe(
  z.object({
    blocked_count: z.int(),
    bucket_start_ns: z.string(),
    event_count: z.int(),
    failure_count: z.int(),
    pending_count: z.int(),
    target_id: z.string(),
    target_kind: ToolUsageTargetTimeSeriesPointTargetKind$inboundSchema,
    target_label: z.string(),
    target_type: ToolUsageTargetTimeSeriesPointTargetType$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "blocked_count": "blockedCount",
      "bucket_start_ns": "bucketStartNs",
      "event_count": "eventCount",
      "failure_count": "failureCount",
      "pending_count": "pendingCount",
      "target_id": "targetId",
      "target_kind": "targetKind",
      "target_label": "targetLabel",
      "target_type": "targetType",
    });
  }),
);

export function toolUsageTargetTimeSeriesPointFromJSON(
  jsonString: string,
): SafeParseResult<ToolUsageTargetTimeSeriesPoint, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ToolUsageTargetTimeSeriesPoint$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ToolUsageTargetTimeSeriesPoint' from JSON`,
  );
}
